import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import ImovelAPI from 'dashboard/api/imoveis';
import { useAlert } from 'dashboard/composables';
import { ENTRY_ACCESS_METADATA_FIELDS } from '../utils/imovelShowConstants';
import {
  actionErrorMessage,
  confirmSensitiveAction,
  entryAccessKindLabel,
  normalizePayload,
} from '../utils/imovelShowFormatters';

/**
 * CRUD de acessos de entrada (chaves) do imóvel, com permissão, sucesso e erro.
 *
 */
export function useImovelEntryAccess() {
  const route = useRoute();

  const entryAccesses = ref([]);
  const entryInformation = ref(null);
  const entryAccessLoading = ref(false);
  const entryAccessSaving = ref(false);
  const entryAccessDeletingId = ref('');
  const entryAccessError = ref('');
  const entryAccessPermissionDenied = ref(false);
  const entryAccessSuccess = ref('');
  const editingEntryAccessId = ref('');
  const entryAccessForm = reactive({
    access_kind: 'chave_com_corretor',
    role: 'AGENT',
    assignee_name: '',
    assignee_phone: '',
    assignee_email: '',
    instructions: '',
    keys_with_agent: true,
    opt_in: true,
    metadata: {
      doorman_type: '',
      house_occupancy: '',
      house_availability: '',
      vacant_at: '',
      key_type: 'COMMON',
      responsible_address: '',
    },
  });

  const normalizedEntryAccesses = computed(() =>
    entryAccesses.value.map(entry => normalizePayload(entry))
  );
  const entryAccessEmpty = computed(
    () =>
      !entryAccessLoading.value &&
      !entryAccessError.value &&
      !entryAccessPermissionDenied.value &&
      !normalizedEntryAccesses.value.length
  );
  const isEditingEntryAccess = computed(() =>
    Boolean(editingEntryAccessId.value)
  );
  const entryAccessSubmitLabel = computed(() =>
    isEditingEntryAccess.value ? 'Salvar acesso' : 'Criar acesso'
  );
  const canSubmitEntryAccess = computed(() =>
    Boolean(entryAccessForm.access_kind)
  );
  const entryInformationPayload = computed(() =>
    normalizePayload(entryInformation.value || {})
  );
  const entryInformationHistory = computed(() => {
    const records =
      entryInformationPayload.value.visitHistory ||
      entryInformationPayload.value.visit_history ||
      [];
    return Array.isArray(records)
      ? records.map(record => normalizePayload(record)).slice(0, 5)
      : [];
  });
  const selectedEntryAccessAuditLabel = computed(() => {
    if (!normalizedEntryAccesses.value.length) {
      return 'Sem acessos cadastrados no contrato entry_accesses.';
    }

    const withAgent = normalizedEntryAccesses.value.filter(
      entry => entry.keysWithAgent
    ).length;
    return `${normalizedEntryAccesses.value.length} acesso(s) cadastrados · ${withAgent} com chaves com corretor`;
  });

  const fetchEntryAccesses = async () => {
    if (!route.params.id) return;

    entryAccessLoading.value = true;
    entryAccessError.value = '';
    entryAccessPermissionDenied.value = false;
    try {
      const { data } = await ImovelAPI.entryAccesses(route.params.id);
      entryAccesses.value = Array.isArray(data)
        ? data.map(entry => normalizePayload(entry))
        : [];
    } catch (error) {
      entryAccesses.value = [];
      if (error?.response?.status === 403) {
        entryAccessPermissionDenied.value = true;
        entryAccessError.value =
          'Você não tem permissão para gerenciar acessos deste imóvel.';
        return;
      }

      entryAccessError.value = actionErrorMessage(
        error,
        'Não foi possível carregar acessos e chaves.'
      );
    } finally {
      entryAccessLoading.value = false;
    }
  };

  const fetchEntryInformation = async () => {
    if (!route.params.id) return;

    try {
      const { data } = await ImovelAPI.entryInformation(route.params.id);
      entryInformation.value = data?.payload || data || null;
    } catch {
      entryInformation.value = null;
    }
  };

  const clearEntryAccessForm = () => {
    editingEntryAccessId.value = '';
    entryAccessForm.access_kind = 'chave_com_corretor';
    entryAccessForm.role = 'AGENT';
    entryAccessForm.assignee_name = '';
    entryAccessForm.assignee_phone = '';
    entryAccessForm.assignee_email = '';
    entryAccessForm.instructions = '';
    entryAccessForm.keys_with_agent = true;
    entryAccessForm.opt_in = true;
    ENTRY_ACCESS_METADATA_FIELDS.forEach(field => {
      entryAccessForm.metadata[field] = field === 'key_type' ? 'COMMON' : '';
    });
  };

  const editEntryAccess = entry => {
    const metadata = entry.metadata || {};
    editingEntryAccessId.value = entry.id;
    entryAccessError.value = '';
    entryAccessSuccess.value = '';
    entryAccessForm.access_kind = entry.kind || 'chave_com_corretor';
    entryAccessForm.role = entry.role || '';
    entryAccessForm.assignee_name = entry.assignee?.name || '';
    entryAccessForm.assignee_phone = entry.assignee?.phone || '';
    entryAccessForm.assignee_email = entry.assignee?.email || '';
    entryAccessForm.instructions = entry.instructions || '';
    entryAccessForm.keys_with_agent = Boolean(entry.keysWithAgent);
    entryAccessForm.opt_in = entry.optIn !== false;
    ENTRY_ACCESS_METADATA_FIELDS.forEach(field => {
      entryAccessForm.metadata[field] =
        metadata[field] || (field === 'key_type' ? 'COMMON' : '');
    });
  };

  const entryAccessPayload = () => ({
    access_kind: entryAccessForm.access_kind,
    role: entryAccessForm.role || undefined,
    assignee_name: entryAccessForm.assignee_name.trim(),
    assignee_phone: entryAccessForm.assignee_phone.trim(),
    assignee_email: entryAccessForm.assignee_email.trim(),
    instructions: entryAccessForm.instructions.trim(),
    keys_with_agent: entryAccessForm.keys_with_agent,
    opt_in: entryAccessForm.opt_in,
    metadata: Object.fromEntries(
      ENTRY_ACCESS_METADATA_FIELDS.map(field => [
        field,
        entryAccessForm.metadata[field],
      ]).filter(([, value]) => value !== '' && value !== null)
    ),
  });

  const upsertEntryAccessInList = entry => {
    const normalized = normalizePayload(entry);
    const current = normalizedEntryAccesses.value.filter(
      item => item.id !== normalized.id
    );
    entryAccesses.value = [normalized, ...current];
  };

  const submitEntryAccess = async () => {
    entryAccessError.value = '';
    entryAccessSuccess.value = '';
    if (!canSubmitEntryAccess.value) {
      entryAccessError.value = 'Selecione o tipo de acesso antes de salvar.';
      return;
    }

    entryAccessSaving.value = true;
    try {
      const payload = entryAccessPayload();
      const request = isEditingEntryAccess.value
        ? ImovelAPI.updateEntryAccess(
            route.params.id,
            editingEntryAccessId.value,
            payload
          )
        : ImovelAPI.createEntryAccess(route.params.id, payload);
      const { data } = await request;
      upsertEntryAccessInList(data);
      entryAccessSuccess.value = isEditingEntryAccess.value
        ? 'Acesso atualizado com registro operacional.'
        : 'Acesso criado com registro operacional.';
      useAlert(entryAccessSuccess.value);
      clearEntryAccessForm();
      await fetchEntryInformation();
    } catch (error) {
      if (error?.response?.status === 403) {
        entryAccessPermissionDenied.value = true;
        entryAccessError.value =
          'Você não tem permissão para salvar acessos deste imóvel.';
      } else {
        entryAccessError.value = actionErrorMessage(
          error,
          'Não foi possível salvar o acesso.'
        );
      }
      useAlert(entryAccessError.value);
    } finally {
      entryAccessSaving.value = false;
    }
  };

  const deleteEntryAccess = async entry => {
    if (!entry?.id) return;
    if (
      !confirmSensitiveAction(
        `Excluir ${entry.accessLabel || entryAccessKindLabel(entry.kind)}? O ledger de chaves deve ser revisado antes de remover este acesso.`
      )
    ) {
      return;
    }

    entryAccessDeletingId.value = entry.id;
    entryAccessError.value = '';
    entryAccessSuccess.value = '';
    try {
      await ImovelAPI.deleteEntryAccess(route.params.id, entry.id);
      entryAccesses.value = normalizedEntryAccesses.value.filter(
        item => item.id !== entry.id
      );
      entryAccessSuccess.value =
        'Acesso removido. Revise o ledger de custódia, se houver chave vinculada.';
      useAlert(entryAccessSuccess.value);
      if (editingEntryAccessId.value === entry.id) clearEntryAccessForm();
      await fetchEntryInformation();
    } catch (error) {
      entryAccessError.value = actionErrorMessage(
        error,
        'Não foi possível excluir o acesso.'
      );
      useAlert(entryAccessError.value);
    } finally {
      entryAccessDeletingId.value = '';
    }
  };

  return {
    entryAccesses,
    entryInformation,
    entryAccessLoading,
    entryAccessSaving,
    entryAccessDeletingId,
    entryAccessError,
    entryAccessPermissionDenied,
    entryAccessSuccess,
    entryAccessForm,
    normalizedEntryAccesses,
    entryAccessEmpty,
    isEditingEntryAccess,
    entryAccessSubmitLabel,
    canSubmitEntryAccess,
    entryInformationHistory,
    selectedEntryAccessAuditLabel,
    fetchEntryAccesses,
    fetchEntryInformation,
    clearEntryAccessForm,
    editEntryAccess,
    submitEntryAccess,
    deleteEntryAccess,
  };
}

export default useImovelEntryAccess;
